import { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import ProductCard, { productImage } from "@/components/ProductCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useMedia } from "@/context/MediaContext";
import { useProducts } from "@/context/ProductsContext";
import { useCart, VARIANTS } from "@/context/CartContext";

const ACCORDION_DEFAULT = ["description"];
const NOTE_TIERS = [
  { key: "top", label: "Top Notes" },
  { key: "heart", label: "Heart Notes" },
  { key: "base", label: "Base Notes" },
];

export default function ProductPage() {
  const { slug } = useParams();
  const { products } = useProducts();
  const img = useMedia();
  const { addItem } = useCart();
  const [active, setActive] = useState(0);
  const [variant, setVariant] = useState("50ml");
  const [qty, setQty] = useState(1);

  useEffect(() => { setActive(0); setVariant("50ml"); setQty(1); }, [slug]);

  const p = products.find((x) => x.slug === slug);
  if (!p) return <Navigate to="/collections" replace />;

  const soldOut = p.available === false;
  const bottleSrc = productImage(p, img);
  const gallery = [bottleSrc, ...(p.images || []).slice(1), p.aestheticImage, p.notesImage].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i);
  const current = gallery[active] || gallery[0];
  const price = p[VARIANTS[variant].priceKey];
  const related = products.filter((x) => x.slug !== slug).slice(0, 4);
  const collectionTitle = { "for-him": "For Him", "for-her": "For Her", unisex: "Unisex" }[p.collection];

  return (
    <main data-testid={`product-page-${p.slug}`} className="pt-20 md:pt-24 min-h-screen">
      <Seo title={`${p.name} — Reserve 30 Parfums`} description={p.description} image={bottleSrc} />
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 pb-28 lg:pb-28">
        <nav data-testid="product-breadcrumb" className="py-5 flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.2em] text-black/45 overflow-x-auto no-scrollbar whitespace-nowrap">
          <Link to="/" className="hover:text-black transition-colors">Home</Link>
          <ChevronRight size={11} strokeWidth={1.5} />
          <Link to={`/collections/${p.collection}`} className="hover:text-black transition-colors">{collectionTitle}</Link>
          <ChevronRight size={11} strokeWidth={1.5} />
          <span className="text-black/80">{p.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-7 min-w-0 flex flex-col-reverse md:flex-row gap-4">
            <div className="flex md:flex-col gap-3 md:w-20 shrink-0 min-w-0 max-w-full overflow-x-auto no-scrollbar">
              {gallery.map((src, i) => (
                <button
                  key={src}
                  type="button"
                  onClick={() => setActive(i)}
                  data-testid={`product-page-thumb-${i}`}
                  aria-label={`Show image ${i + 1}`}
                  className={`shrink-0 w-16 md:w-full aspect-[4/5] overflow-hidden bg-[#EFE9E1] border transition-colors duration-300 ${i === active ? "border-[#BA8D53]" : "border-transparent opacity-60 hover:opacity-100"}`}
                >
                  <img src={src} alt={`${p.name} view ${i + 1}`} loading="lazy" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
            <div className="relative flex-1 overflow-hidden bg-[#EFE9E1]">
              <div className="aspect-[4/5] lg:aspect-auto lg:h-[78vh]">
                <img key={current} src={current} alt={p.name} data-testid="product-page-main-image" className={`h-full w-full object-cover ${soldOut ? "opacity-60 grayscale" : ""}`} />
              </div>
              {soldOut && <span className="absolute top-4 left-4 bg-[#111]/85 text-[#F7F3EE] px-3 py-1.5 text-[0.6rem] uppercase tracking-[0.24em]">Sold Out</span>}
              {!soldOut && p.bestseller && <span data-testid={`bestseller-badge-page-${p.slug}`} className="absolute top-4 left-4 bg-[#BA8D53] text-white px-3 py-1.5 text-[0.6rem] uppercase tracking-[0.24em]">Best Seller</span>}
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <p className="text-[0.62rem] uppercase tracking-[0.26em] text-[#BA8D53]" data-testid="product-page-accord">{p.accord}</p>
              <h1 className="font-serif-brand text-5xl md:text-6xl font-light mt-3 leading-[0.95]">{p.name}</h1>
              <p className="mt-3 text-sm text-black/55">{variant === "combo" ? "Parfum + Roll-On set" : "Parfum · Extrait strength"}</p>

              <div className="mt-8">
                <p className="text-[0.62rem] uppercase tracking-[0.24em] text-black/50 mb-3">Size</p>
                <div className="flex flex-wrap gap-2" data-testid="product-page-sizes">
                  {Object.entries(VARIANTS).map(([k, cfg]) => {
                    if (p[cfg.priceKey] == null) return null;
                    const on = variant === k;
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setVariant(k)}
                        data-testid={`page-size-${k}`}
                        className={`px-4 py-2.5 text-[0.65rem] uppercase tracking-[0.18em] border transition-colors duration-300 ${on ? "bg-[#111] text-[#F7F3EE] border-[#111]" : "border-black/20 hover:border-black"}`}
                      >
                        {cfg.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-8 flex items-end justify-between border-t border-black/10 pt-6">
                <p data-testid={`page-price-${variant}-${p.slug}`} className="font-serif-brand text-3xl font-light">
                  ₹{Math.round(price ?? 0).toLocaleString("en-IN")}
                </p>
                <div className="flex items-center border border-black/20" data-testid="product-page-qty">
                  <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-minus" className="h-10 w-10 hover:bg-[#EFE9E1] transition-colors">−</button>
                  <span className="w-8 text-center text-sm tabular-nums" data-testid="qty-value">{qty}</span>
                  <button type="button" onClick={() => setQty((q) => Math.min(10, q + 1))} data-testid="qty-plus" className="h-10 w-10 hover:bg-[#EFE9E1] transition-colors">+</button>
                </div>
              </div>

              <div className="mt-6">
                {soldOut ? (
                  <span className="btn-ink w-full justify-center !py-4 opacity-40 pointer-events-none">Sold Out</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => { for (let i = 0; i < qty; i += 1) addItem(p.slug, variant); }}
                    data-testid={`page-add-to-cart-${p.slug}-${variant}`}
                    className="btn-ink w-full justify-center !py-4"
                  >
                    Add to Cart
                  </button>
                )}
                <p className="mt-3 text-xs text-black/45 text-center">Complimentary delivery above ₹999 · Secure Razorpay checkout</p>
              </div>

              <Accordion type="multiple" defaultValue={ACCORDION_DEFAULT} className="mt-10" data-testid="product-page-accordion">
                <AccordionItem value="description" className="border-black/10">
                  <AccordionTrigger className="text-[0.68rem] uppercase tracking-[0.22em] font-medium hover:no-underline py-5">Description</AccordionTrigger>
                  <AccordionContent className="text-sm text-black/60 leading-relaxed">{p.description}</AccordionContent>
                </AccordionItem>
                {p.notes && (
                  <AccordionItem value="notes" className="border-black/10">
                    <AccordionTrigger className="text-[0.68rem] uppercase tracking-[0.22em] font-medium hover:no-underline py-5">Olfactory Notes</AccordionTrigger>
                    <AccordionContent data-testid={`product-page-notes-${p.slug}`}>
                      <div className="grid grid-cols-3 gap-4">
                        {NOTE_TIERS.map((t) => (
                          <div key={t.key}>
                            <p className="text-[0.58rem] uppercase tracking-[0.24em] text-[#BA8D53]">{t.label}</p>
                            <p className="mt-1.5 font-serif-brand text-lg font-light leading-snug">{p.notes[t.key]}</p>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}
                <AccordionItem value="ritual" className="border-black/10">
                  <AccordionTrigger className="text-[0.68rem] uppercase tracking-[0.22em] font-medium hover:no-underline py-5">The Reserve 30 Ritual</AccordionTrigger>
                  <AccordionContent className="text-sm text-black/60 leading-relaxed">
                    Matured 8–10 weeks before bottling. Apply to pulse points on bare skin; do not rub. Parfum-strength concentration lasts 10–12 hours.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="shipping" className="border-black/10">
                  <AccordionTrigger className="text-[0.68rem] uppercase tracking-[0.22em] font-medium hover:no-underline py-5">Shipping & Returns</AccordionTrigger>
                  <AccordionContent className="text-sm text-black/60 leading-relaxed">
                    Dispatched within 24–48 hours across India. Free delivery above ₹999.{" "}
                    <Link to="/policies/returns" className="link-line text-black">Read our return policy</Link>.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </Reveal>
        </div>

        {related.length > 0 && (
          <div className="mt-20 lg:mt-28" data-testid="related-fragrances-section">
            <Reveal className="mb-12">
              <p className="overline-label">Complete the wardrobe</p>
              <h2 className="font-serif-brand text-3xl md:text-4xl font-light mt-3">You may also like</h2>
            </Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
              {related.map((r, i) => (
                <Reveal key={r.slug} delay={i * 0.08}>
                  <ProductCard p={r} />
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </div>

      {!soldOut && (
        <div data-testid="mobile-sticky-cart" className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-[#F7F3EE]/95 backdrop-blur-md border-t border-black/10 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center gap-3">
          <div className="min-w-0">
            <p className="font-serif-brand text-base leading-tight truncate">{p.name}</p>
            <p className="text-xs text-black/55">{VARIANTS[variant].label} · <span className="text-black font-medium">₹{Math.round(price ?? 0).toLocaleString("en-IN")}</span></p>
          </div>
          <button
            type="button"
            onClick={() => { for (let i = 0; i < qty; i += 1) addItem(p.slug, variant); }}
            data-testid={`mobile-add-to-cart-${p.slug}`}
            className="ml-auto shrink-0 bg-[#111] text-[#F7F3EE] px-6 h-12 text-[0.65rem] uppercase tracking-[0.22em] font-medium"
          >
            Add to Cart
          </button>
        </div>
      )}
    </main>
  );
}
